import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { AddDriveImage, AddVehicle, UpdateVehicle } from "../../api/apirequest";

const UserForm = ({ onClose, onSubmit, editData, isEdit, fetchData }) => {
  const [formData, setFormData] = useState({
    VregNO: "",
    VehicleID: 0,
    Model: "",
    Make: "",
    InspectionDate: "",
    BookedDate: "",
    VehicleStatus: "Available", // Assuming "statusname" was meant to be VehicleStatus
    DateAdded: new Date().toISOString().split("T")[0],
    StatusID: 1,
    documents: null,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (editData) {
      setFormData({
        ...editData,
        DateAdded: editData.DateAdded
          ? editData.DateAdded.split("T")[0]
          : new Date().toISOString().split("T")[0],
        InspectionDate: editData.InspectionDate
          ? editData.InspectionDate.split("T")[0]
          : "",
        BookedDate: editData.BookedDate
          ? editData.BookedDate.split("T")[0]
          : "",
        Model: editData.Model || "",
        Make: editData.Make || "",
        VehicleStatus: editData.VehicleStatus || "Available",
        StatusID: editData.StatusID || 1,
        VehicleID: editData.VehicleID || null,
        documents: null,
      });
    }
  }, [editData]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      if (!formData.VregNO) {
        setError("Please fill in the Registration Number.");
        setLoading(false);
        return;
      }

      let documentId = null;
      if (formData.documents) {
        const reader = new FileReader();
        reader.readAsDataURL(formData.documents);
        await new Promise((resolve) => {
          reader.onload = async () => {
            const base64Document = reader.result.split(",")[1];
            const documentResult = await AddDriveImage({
              base64Image: base64Document,
              fileName: `${formData.VregNO}_${new Date().toISOString()}`,
            });

            if (documentResult.ViewLink) {
              documentId = documentResult.FileId;
            } else {
              toast.error("Failed to upload vehicle document.");
              setLoading(false);
              return;
            }
            resolve();
          };
        });
      }

      let result;
      if (isEdit && formData.VehicleID) {
        const updateData = {
          VehicleID: formData.VehicleID,
          VregNO: formData.VregNO,
          Model: formData.Model,
          AssignedDriver: 0,
          Make: formData.Make,
          InspectionDate: formData.InspectionDate,
          BookedDate: formData.BookedDate,
          VehicleStatus: formData.VehicleStatus,
          StatusID: formData.StatusID,
          DateUpdated: new Date().toISOString(),
          ...(documentId && { DocumentID: documentId }),
        };

        result = await UpdateVehicle(updateData);

        if (result && result.success) {
          toast.success(result.message || "Vehicle successfully updated!");
        } else {
          toast.error(
            result.message || "An error occurred while updating the vehicle."
          );
        }
      } else {
        const addData = {
          VregNO: formData.VregNO,
          Model: formData.Model,
          Make: formData.Make,
          InspectionDate: formData.InspectionDate,
          AssignedDriver: 0,
          BookedDate: formData.BookedDate,
          VehicleStatus: formData.VehicleStatus,
          DateAdded: formData.DateAdded,
          StatusID: formData.StatusID,
          ...(documentId && { DocumentID: documentId }),
        };

        result = await AddVehicle(addData);

        if (result && result.success) {
          toast.success(result.message || "Vehicle successfully added!");
        } else {
          toast.error(
            result.message || "An error occurred while adding the vehicle."
          );
        }
      }

      fetchData();
      setFormData({
        VregNO: "",
        Model: "",
        Make: "",
        InspectionDate: "",
        BookedDate: "",
        VehicleStatus: "Available",
        DateAdded: new Date().toISOString().split("T")[0],
        StatusID: 1,
        VehicleID: null,
        documents: null,
      });
      onClose(false);
    } catch (error) {
      console.error(error);
      toast.error("There was an error processing your request.");
    } finally {
      setLoading(false);
    }
  };

  const isInspectionEditable = isEdit && formData.VehicleStatus === "Booked";

  const styles = {
    form: {
      padding: "20px",
      maxWidth: "600px",
      margin: "0 auto",
    },
    formGroup: {
      marginBottom: "20px",
    },
    label: {
      display: "block",
      marginBottom: "8px",
      fontWeight: "500",
      color: "#374151",
    },
    input: {
      width: "100%",
      padding: "8px 12px",
      border: "1px solid #d1d5db",
      borderRadius: "6px",
      fontSize: "16px",
    },
    disabledInput: {
      width: "100%",
      padding: "8px 12px",
      border: "1px solid #d1d5db",
      borderRadius: "6px",
      fontSize: "16px",
      backgroundColor: "#f3f4f6",
      color: "#374151",
    },
    select: {
      width: "100%",
      padding: "8px 12px",
      border: "1px solid #d1d5db",
      borderRadius: "6px",
      fontSize: "16px",
      backgroundColor: "white",
    },
    statusText: {
      padding: "8px 12px",
      border: "1px solid #d1d5db",
      borderRadius: "6px",
      backgroundColor: "#f3f4f6",
      color: "#374151",
    },
    buttonGroup: {
      display: "flex",
      gap: "12px",
      marginTop: "24px",
    },
    submitButton: {
      padding: "10px 20px",
      backgroundColor: "#3b82f6",
      color: "white",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      fontWeight: "500",
    },
    cancelButton: {
      padding: "10px 20px",
      backgroundColor: "#ef4444",
      color: "white",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      fontWeight: "500",
    },
  };

  return (
    <form onSubmit={handleSubmit} style={styles.form}>
      <div style={styles.formGroup}>
        <label style={styles.label}>Registration Number</label>
        <input
          type="text"
          name="VregNO"
          value={formData.VregNO || ""}
          onChange={handleChange}
          style={styles.input}
          required
        />
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>Model</label>
        <input
          type="text"
          name="Model"
          value={formData.Model || ""}
          onChange={handleChange}
          style={styles.input}
        />
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>Make</label>
        <input
          type="text"
          name="Make"
          value={formData.Make || ""}
          onChange={handleChange}
          style={styles.input}
        />
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>Vehicle Status</label>
        {isEdit ? (
          <div style={styles.statusText}>{formData.VehicleStatus}</div>
        ) : (
          <input
            type="text"
            name="VehicleStatus"
            value={formData.VehicleStatus || "Available"}
            style={styles.disabledInput}
            disabled
          />
        )}
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>Booked Date</label>
        <input
          type="date"
          name="BookedDate"
          value={formData.BookedDate || ""}
          onChange={handleChange}
          style={styles.input}
        />
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>Inspection Date</label>
        <input
          type="date"
          name="InspectionDate"
          value={formData.InspectionDate || ""}
          onChange={handleChange}
          style={isInspectionEditable ? styles.input : styles.disabledInput}
          disabled={!isInspectionEditable && isEdit}
        />
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>Attach Documents</label>
        <input
          type="file"
          name="documents"
          onChange={handleChange}
          style={styles.input}
        />
      </div>

      {error && (
        <div style={{ color: "red", marginBottom: "10px" }}>{error}</div>
      )}

      <div style={styles.buttonGroup}>
        <button type="submit" style={styles.submitButton} disabled={loading}>
          {loading
            ? "Processing..."
            : isEdit
            ? "Update Vehicle"
            : "Add Vehicle"}
        </button>
        <button
          type="button"
          onClick={onClose}
          style={styles.cancelButton}
          disabled={loading}
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default UserForm;